import useFinalizarCobranca from "core/features/cobranca/hooks/useFinalizarCobranca";
import useIniciarCobranca from "core/features/cobranca/hooks/useIniciarCobranca";
import { PaginateResult } from "core/resources/types/shared";
import useCobrancas from "features/cobranca/hooks/useCobrancas";
import { CobrancaItem, SituacaoEnum } from "features/cobranca/typings";
import { useCallback, useEffect, useState } from "react";
import { DraggableLocation, DropResult } from "react-beautiful-dnd";
import { useLoading } from "ui/components/GlobalLoading";
import { FiltroCobranca } from "core/features/cobranca/typings";
enum CobrancaListIds {
  EmJuridico = "emJuridico",
  ACobrar = "aCobrar",
  EmCobranca = "emCobranca",
  Ajuizada = "ajuizada",
}

const move = (
  source: CobrancaItem[],
  destination: CobrancaItem[],
  droppableSource: DraggableLocation,
  droppableDestination?: DraggableLocation
) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);
  const [removed] = sourceClone.splice(droppableSource.index, 1);

  if (droppableDestination) {
    destClone.unshift(removed);

    const result: Partial<Record<CobrancaListIds, CobrancaItem[]>> = {};

    result[droppableSource.droppableId as CobrancaListIds] = sourceClone;
    result[droppableDestination.droppableId as CobrancaListIds] = destClone;

    return result;
  }
};

const cobrancaItemReducer = (
  acc: CobrancaItem[],
  item: PaginateResult<CobrancaItem>
) => acc.concat(item.items ?? []);

function useKambanCobrancas({
  filtroCobranca,
}: {
  filtroCobranca: FiltroCobranca;
}) {
  const {
    data: juridico,
    isLoading: isLoadingJuridico,

    hasNextPage: hasNextPageJuridico,
    fetchNextPage: fetchNextPageJuridico,
    isFetchingNextPage: isFetchingNextPageJuridico,
  } = useCobrancas({
    ...filtroCobranca,
    situacao: SituacaoEnum.Juridico,
  });

  const {
    data: andamento,
    isLoading: isLoadingAndamento,

    hasNextPage: hasNextPageAndamento,
    fetchNextPage: fetchNextPageAndamento,
    isFetchingNextPage: isFetchingNextPageAndamento,
  } = useCobrancas({
    ...filtroCobranca,
    situacao: SituacaoEnum.Andamento,
  });

  const {
    data: cobrar,
    isLoading: isLoadingACobrar,

    hasNextPage: hasNextPageACobrar,
    fetchNextPage: fetchNextPageACobrar,
    isFetchingNextPage: isFetchingNextPageACobrar,
  } = useCobrancas({
    ...filtroCobranca,
    situacao: SituacaoEnum.ACobrar,
  });

  const {
    data: unidadeAjuizada,
    isLoading: isLoadingAjuizada,

    hasNextPage: hasNextPageAjuizada,
    fetchNextPage: fetchNextPageAjuizada,
    isFetchingNextPage: isFetchingNextPageAjuizada,
  } = useCobrancas({
    ...filtroCobranca,
    situacao: SituacaoEnum.Ajuizada,
  });

  const { mutate: iniciarCobranca } = useIniciarCobranca();
  const { mutate: finalizarCobranca } = useFinalizarCobranca();

  const [isDragging, setIsDragging] = useState(false);

  const [lists, setLists] = useState({
    [CobrancaListIds.ACobrar]:
      cobrar?.pages.reduce(cobrancaItemReducer, []) || [],
    [CobrancaListIds.EmCobranca]:
      andamento?.pages.reduce(cobrancaItemReducer, []) || [],
    [CobrancaListIds.EmJuridico]:
      juridico?.pages.reduce(cobrancaItemReducer, []) || [],
    [CobrancaListIds.Ajuizada]:
    unidadeAjuizada?.pages.reduce(cobrancaItemReducer, []) || [],
  });

  const { aCobrar, emCobranca, emJuridico, ajuizada } = lists;

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const { source, destination } = result;
      setIsDragging(false);
      if (!destination || source.droppableId === destination.droppableId) {
        return;
      }

      const item = lists[source.droppableId as CobrancaListIds]?.find(
        (_, index) => index === source.index
      );

      if (
        source.droppableId === "aCobrar" &&
        destination.droppableId === "emCobranca" &&
        item
      ) {
        iniciarCobranca(item.id);
      } else if (
        source.droppableId === "emCobranca" &&
        destination.droppableId === "aCobrar" &&
        item
      ) {
        finalizarCobranca(item.id);
      }

      const newArrays = move(
        lists[source.droppableId as CobrancaListIds],
        lists[destination.droppableId as CobrancaListIds],
        source,
        destination
      );

      setLists((state) => ({ ...state, ...newArrays }));
    },
    [finalizarCobranca, iniciarCobranca, lists]
  );

  useEffect(() => {
    setLists({
      [CobrancaListIds.ACobrar]:
        cobrar?.pages.reduce(cobrancaItemReducer, []) || [],
      [CobrancaListIds.EmCobranca]:
        andamento?.pages.reduce(cobrancaItemReducer, []) || [],
      [CobrancaListIds.EmJuridico]:
        juridico?.pages.reduce(cobrancaItemReducer, []) || [],
      [CobrancaListIds.Ajuizada]:
        unidadeAjuizada?.pages.reduce(cobrancaItemReducer, []) || [],
    });
  }, [cobrar, andamento, juridico, unidadeAjuizada]);

  useLoading(isLoadingJuridico || isLoadingACobrar || isLoadingAndamento || isLoadingAjuizada);

  return {
    onDragEnd,
    aCobrar,
    emJuridico,
    emCobranca,
    unidadeAjuizada,
    isDragging,
    setIsDragging,
    isLoadingJuridico,
    isLoadingAndamento,
    isLoadingACobrar,
    isLoadingAjuizada,
    fetchNextPageACobrar,
    fetchNextPageAndamento,
    fetchNextPageJuridico,
    fetchNextPageAjuizada,
    hasNextPageACobrar,
    hasNextPageAndamento,
    hasNextPageJuridico,
    hasNextPageAjuizada,
    isFetchingNextPageAjuizada,
    isFetchingNextPageJuridico,
    isFetchingNextPageACobrar,
    isFetchingNextPageAndamento,
    juridico,
    cobrar,
    andamento,
    ajuizada,
  };
}

export default useKambanCobrancas;
